import axios from 'axios';

const API_BASE = '/api';

class ApiService {
  constructor() {
    const stored = localStorage.getItem('token');
    this.token = stored || null;
    if (stored) {
      axios.defaults.headers.common.Authorization = `Bearer ${stored}`;
    }

    // Inject the "act as tenant" header so a managed-tenant admin's requests are
    // scoped to whichever tenant they've switched to in the console.
    axios.interceptors.request.use((config) => {
      try {
        const t = localStorage.getItem('ce_act_as_tenant');
        if (t) { config.headers = config.headers || {}; config.headers['X-Act-As-Tenant'] = t; }
      } catch (_) {}
      return config;
    });

    // Intercept 401/403 expired-token responses: clear session and reload to login page
    axios.interceptors.response.use(
      response => response,
      error => {
        const status = error?.response?.status;
        const msg = error?.response?.data?.error || '';
        const isTokenError = (status === 401 || status === 403) &&
          (msg.includes('expired') || msg.includes('Invalid') || msg.includes('invalidated') || msg.includes('Session'));
        if (isTokenError) {
          // Clear BOTH localStorage and cookies before reloading so that the
          // cookie-restore path in App.tsx does not immediately re-trigger a 401
          try {
            document.cookie = 'ce_token=; max-age=0; SameSite=Lax; Secure';
            document.cookie = 'ce_sess=; max-age=0; SameSite=Lax; Secure';
          } catch (_) {}
          localStorage.removeItem('token');
          localStorage.removeItem('ce_session');
          delete axios.defaults.headers.common['Authorization'];
          window.location.reload();
        }
        return Promise.reject(error);
      }
    );
  }

  setToken(token) {
    this.token = token;
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  clearToken() {
    try {
      document.cookie = "ce_token=; max-age=0; SameSite=Lax; Secure";
      document.cookie = "ce_sess=; max-age=0; SameSite=Lax; Secure";
    } catch (_) {}
    this.token = null;
    delete axios.defaults.headers.common.Authorization;
    localStorage.removeItem('token');
    localStorage.removeItem('ce_session');
  }

  getToken() {
    return this.token;
  }

  // Revoke the server-side session before clearing local state.
  // Always resolves so callers don't need to catch.
  async logout() {
    try {
      await axios.post(`${API_BASE}/logout`);
    } catch (_) {
      // Server may be unreachable — still clear local state
    }
    this.clearToken();
  }

  async login(username, password, tenantSubdomain, totp, trustDevice) {
    const body = { username, password };
    if (tenantSubdomain) body.tenant_subdomain = tenantSubdomain;
    if (totp) body.totp = totp;
    // Trusted-device 2FA skip: always present the stored device token so a
    // previously-trusted browser bypasses the 2FA prompt; ask to trust when set.
    try { const dt = localStorage.getItem('ce_device_token'); if (dt) body.device_token = dt; } catch (_) {}
    if (trustDevice) body.trust_device = true;
    const response = await axios.post(`${API_BASE}/login`, body);

    if (response.data && response.data.device_token) {
      try { localStorage.setItem('ce_device_token', response.data.device_token); } catch (_) {}
    }
    if (response.data.token) {
      this.setToken(response.data.token);
    }

    return response.data;
  }

  async getAllowedCLI() {
    const response = await axios.get(`${API_BASE}/allowed-cli`);
    return response.data;
  }

  /*
    CONTACTS
  */

  async getContacts() {
    const response = await axios.get(`${API_BASE}/contacts`);
    return response.data;
  }

  // Company phone directory — internal users/extensions of the caller's tenant.
  async getDirectory() {
    const response = await axios.get(`${API_BASE}/directory`);
    return response.data;
  }

  async addContact(data) {
    const response = await axios.post(`${API_BASE}/contacts`, data);
    return response.data;
  }

  async updateContact(id, data) {
    const response = await axios.put(`${API_BASE}/contacts/${id}`, data);
    return response.data;
  }

  async updateLastDialed(id) {
    const response = await axios.post(`${API_BASE}/contacts/${id}/last-dialed`);
    return response.data;
  }

  async updateContactLastDialed(id) {
    const response = await axios.post(`${API_BASE}/contacts/${id}/last-dialed`);
    return response.data;
  }

  async deleteContact(id) {
    const response = await axios.delete(`${API_BASE}/contacts/${id}`);
    return response.data;
  }

  /*
    CALL HISTORY
  */

  async getCallHistory(limit = 50) {
    const response = await axios.get(`${API_BASE}/call-history?limit=${limit}`);
    return response.data;
  }

  async getSupervisorCallHistory(limit = 200) {
    const response = await axios.get(`${API_BASE}/supervisor/call-history?limit=${limit}`);
    return response.data;
  }

  async saveCallHistory(data) {
    const response = await axios.post(`${API_BASE}/call-history`, data);
    return response.data;
  }

  /*
    RECORDINGS
  */

  async uploadRecording(blob, filename) {
    const formData = new FormData();
    formData.append('recording', blob, filename);

    const response = await axios.post(
      `${API_BASE}/recordings/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  }

  async getRecordings() {
    const response = await axios.get(`${API_BASE}/recordings`);
    return response.data;
  }

  async deleteRecording(id) {
    const response = await axios.delete(`${API_BASE}/recordings/${id}`);
    return response.data;
  }

  getRecordingUrl(url) {
    if (!url) return '';

    const token = this.getToken();

    if (!token) return url;

    return `${url}?token=${encodeURIComponent(token)}`;
  }

  /*
    PBX / WALLBOARD
  */

  async getPBXWallboard() {
    const response = await axios.get(`${API_BASE}/pbx/wallboard`);
    return response.data;
  }

  async getWallboard() {
    const response = await axios.get(`${API_BASE}/pbx/wallboard`);
    return response.data;
  }

  /*
    AGENT STATE
  */

  async getPauseReasons() {
    const response = await axios.get(
      `${API_BASE}/agent/pause-reasons`
    );

    return response.data;
  }

  async updateAgentState(data) {
    const response = await axios.post(
      `${API_BASE}/agent/state`,
      data
    );

    return response.data;
  }

  async getAgentEvents() {
    const response = await axios.get(
      `${API_BASE}/supervisor/agent-events`
    );

    return response.data;
  }

  async getLiveAgents() {
    const response = await axios.get(
      `${API_BASE}/supervisor/live-agents`
    );

    return response.data;
  }

  /*
    ADMIN PANEL
  */

  async getAdminUsers() {
    const response = await axios.get(`${API_BASE}/admin-panel/users`);
    return response.data;
  }

  async createAdminUser(data) {
    const response = await axios.post(`${API_BASE}/admin-panel/users`, data);
    return response.data;
  }

  async setTrunkSuperAdminOnly(id, on) {
    const response = await axios.put(`${API_BASE}/trunks/${id}/super-admin-only`, { super_admin_only: !!on });
    return response.data;
  }

  async getPbxSites() {
    const response = await axios.get(`${API_BASE}/pbx-sites`);
    return response.data;
  }

  async getPbxSiteSummary(id) {
    const response = await axios.get(`${API_BASE}/pbx-sites/${id}/summary`);
    return response.data;
  }

  async createPbxSite(data) {
    const response = await axios.post(`${API_BASE}/pbx-sites`, data);
    return response.data;
  }

  async updatePbxSite(id, data) {
    const response = await axios.put(`${API_BASE}/pbx-sites/${id}`, data);
    return response.data;
  }

  async deletePbxSite(id) {
    const response = await axios.delete(`${API_BASE}/pbx-sites/${id}`);
    return response.data;
  }

  async getNewUserDefaults(siteId) {
    const response = await axios.get(`${API_BASE}/admin-panel/users/new-user-defaults`, { params: siteId ? { site_id: siteId } : {} });
    return response.data;
  }

  async updateAdminUser(id, data) {
    const response = await axios.put(`${API_BASE}/admin-panel/users/${id}`, data);
    return response.data;
  }

  async resetAdminUserPassword(id, password) {
    const response = await axios.post(
      `${API_BASE}/admin-panel/users/${id}/reset-password`,
      { password }
    );

    return response.data;
  }

  async deleteAdminUser(id) {
    const response = await axios.delete(`${API_BASE}/admin-panel/users/${id}`);
    return response.data;
  }

  // Generic HTTP helpers for ad-hoc endpoints
  async get(url) { const r = await axios.get(url); return r; }
  async post(url, data) { const r = await axios.post(url, data); return r; }
  async put(url, data) { const r = await axios.put(url, data); return r; }
  async delete(url) { const r = await axios.delete(url); return r; }

  /*
    QUEUES
  */

  async getQueues() {
    const response = await axios.get(`${API_BASE}/queues`);
    return response.data;
  }

  async createQueue(data) {
    const response = await axios.post(`${API_BASE}/queues`, data);
    return response.data;
  }

  async updateQueue(id, data) {
    const response = await axios.put(`${API_BASE}/queues/${id}`, data);
    return response.data;
  }

  async deleteQueue(id) {
    const response = await axios.delete(`${API_BASE}/queues/${id}`);
    return response.data;
  }

  async getQueueMembers(id) {
    const response = await axios.get(`${API_BASE}/queues/${id}/members`);
    return response.data;
  }

  async addQueueMember(queueId, data) {
    const response = await axios.post(`${API_BASE}/queues/${queueId}/members`, data);
    return response.data;
  }

  async removeQueueMember(queueId, userId) {
    const response = await axios.delete(`${API_BASE}/queues/${queueId}/members/${userId}`);
    return response.data;
  }

  async getQueueStats() {
    const response = await axios.get(`${API_BASE}/queue-stats`);
    return response.data;
  }

  async getQueueSessions() {
    const response = await axios.get(
      `${API_BASE}/supervisor/queue-sessions`
    );

    return response.data;
  }

  async getLiveCalls() {
    const response = await axios.get(`${API_BASE}/monitor/live-calls`);
    return response.data;
  }

  async getSecurityBans() {
    const response = await axios.get(`${API_BASE}/security/bans`);
    return response.data;
  }

  async getCdr(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') q.append(k, v); });
    const response = await axios.get(`${API_BASE}/cdr?${q.toString()}`);
    return response.data;
  }

  /*
    SUPERVISOR
  */

  async getSupervisorAgents() {
    const response = await axios.get(`${API_BASE}/supervisor/agents`);
    return response.data;
  }

  async getSupervisorCalls() {
    const response = await axios.get(`${API_BASE}/supervisor/calls`);
    return response.data;
  }

  async getSupervisorQueues() {
    const response = await axios.get(`${API_BASE}/supervisor/queues`);
    return response.data;
  }

  async getSupervisorQueueAgents() {
    const response = await axios.get(`${API_BASE}/supervisor/queue-agents`);
    return response.data;
  }

  async getSupervisorQueueMembers(queueId) {
    const response = await axios.get(`${API_BASE}/supervisor/queues/${queueId}/members`);
    return response.data;
  }

  async supervisorAddQueueMember(queueId, data) {
    const response = await axios.post(`${API_BASE}/supervisor/queues/${queueId}/members`, data);
    return response.data;
  }

  async supervisorUpdateQueueMember(queueId, userId, data) {
    const response = await axios.patch(`${API_BASE}/supervisor/queues/${queueId}/members/${userId}`, data);
    return response.data;
  }

  async supervisorRemoveQueueMember(queueId, userId) {
    const response = await axios.delete(`${API_BASE}/supervisor/queues/${queueId}/members/${userId}`);
    return response.data;
  }

  async getMyQueueMemberships() {
    const response = await axios.get(`${API_BASE}/agent/my-queue-memberships`);
    return response.data;
  }

  async getSupervisorWallboard() {
    const response = await axios.get(`${API_BASE}/supervisor/wallboard`);
    return response.data;
  }

  /*
    TENANTS
  */


  /*
    TENANT LICENSES (super_admin only)
  */

  async getTenantLicenses() {
    const response = await axios.get(`${API_BASE}/admin/tenant-licenses`);
    return response.data;
  }

  async getTenantLicenseWithUsage(tenantId) {
    const response = await axios.get(`${API_BASE}/admin/tenant-licenses/${tenantId}`);
    return response.data;
  }

  async getTenantLicense(tenantId) {
    const response = await axios.get(`${API_BASE}/admin/tenant-licenses/${tenantId}`);
    return response.data;
  }

  async updateTenantLicense(tenantId, data) {
    const response = await axios.put(`${API_BASE}/admin/tenant-licenses/${tenantId}`, data);
    return response.data;
  }

  async getTenants() {
    const response = await axios.get(`${API_BASE}/admin/tenants`);
    return response.data;
  }

  async createTenant(data) {
    const response = await axios.post(`${API_BASE}/admin/tenants`, data);
    return response.data;
  }

  async updateTenant(id, data) {
    const response = await axios.put(`${API_BASE}/admin/tenants/${id}`, data);
    return response.data;
  }

  async renumberTenantExtensions(id) {
    const response = await axios.post(`${API_BASE}/admin/tenants/${id}/renumber-extensions`);
    return response.data;
  }

  /*
    EXTENSIONS
  */

  async getExtensions() {
    const response = await axios.get(`${API_BASE}/extensions`);
    return response.data;
  }

  async createExtension(data) {
    const response = await axios.post(`${API_BASE}/extensions`, data);
    return response.data;
  }

  async updateExtension(id, data) {
    const response = await axios.put(`${API_BASE}/extensions/${id}`, data);
    return response.data;
  }

  async deleteExtension(id) {
    const response = await axios.delete(`${API_BASE}/extensions/${id}`);
    return response.data;
  }

  /*
    DIDS
  */

  async getDids() {
    const response = await axios.get(`${API_BASE}/dids`);
    return response.data;
  }

  async createDid(data) {
    const response = await axios.post(`${API_BASE}/dids`, data);
    return response.data;
  }

  async updateDid(id, data) {
    const response = await axios.put(`${API_BASE}/dids/${id}`, data);
    return response.data;
  }

  async deleteDid(id) {
    const response = await axios.delete(`${API_BASE}/dids/${id}`);
    return response.data;
  }

  // ── Trunks ──────────────────────────────────────────────────────────────────
  async getTrunks() {
    const response = await axios.get(`${API_BASE}/trunks`);
    return response.data;
  }

  async createTrunk(data) {
    const response = await axios.post(`${API_BASE}/trunks`, data);
    return response.data;
  }

  async updateTrunk(id, data) {
    const response = await axios.put(`${API_BASE}/trunks/${id}`, data);
    return response.data;
  }

  async deleteTrunk(id) {
    const response = await axios.delete(`${API_BASE}/trunks/${id}`);
    return response.data;
  }

  async asteriskSync() {
    const response = await axios.post(`${API_BASE}/asterisk/sync`);
    return response.data;
  }

  // ── Trunk <-> tenant assignment ─────────────────────────────────────────────
  async getTrunkTenants(id) {
    const response = await axios.get(`${API_BASE}/trunks/${id}/tenants`);
    return response.data;
  }

  async setTrunkTenants(id, tenant_ids) {
    const response = await axios.put(`${API_BASE}/trunks/${id}/tenants`, { tenant_ids });
    return response.data;
  }

  async getTrunkProfiles() {
    const response = await axios.get(`${API_BASE}/trunk-profiles`);
    return response.data;
  }

  // ── 2FA + Deploy-to-Production pipeline ─────────────────────────────────────
  async get2faStatus() { return (await axios.get(`${API_BASE}/2fa/status`)).data; }
  async setup2fa()     { return (await axios.post(`${API_BASE}/2fa/setup`)).data; }
  async verify2fa(code){ return (await axios.post(`${API_BASE}/2fa/verify`, { code })).data; }
  async disable2fa(code){ return (await axios.post(`${API_BASE}/2fa/disable`, { code })).data; }
  async deployPreview(){ return (await axios.get(`${API_BASE}/deploy/preview`)).data; }
  async deployHistory(){ return (await axios.get(`${API_BASE}/deploy/history`)).data; }
  async deployExecute(code, dry_run){ return (await axios.post(`${API_BASE}/deploy/execute`, { code, dry_run })).data; }

  async asteriskStatus() {
    const response = await axios.get(`${API_BASE}/asterisk/status`);
    return response.data;
  }

  // IVR Menus
  async getIvrMenus() {
    const r = await axios.get(`${API_BASE}/asterisk/ivr`);
    return r.data;
  }
  async getIvrOptions(menuId) {
    const r = await axios.get(`${API_BASE}/asterisk/ivr/${menuId}/options`);
    return r.data;
  }
  async createIvrMenu(data) {
    const r = await axios.post(`${API_BASE}/asterisk/ivr`, data);
    return r.data;
  }
  async updateIvrMenu(id, data) {
    const r = await axios.put(`${API_BASE}/asterisk/ivr/${id}`, data);
    return r.data;
  }
  async deleteIvrMenu(id) {
    const r = await axios.delete(`${API_BASE}/asterisk/ivr/${id}`);
    return r.data;
  }
  async createIvrOption(menuId, data) {
    const r = await axios.post(`${API_BASE}/asterisk/ivr/${menuId}/options`, data);
    return r.data;
  }
  async deleteIvrOption(menuId, optId) {
    const r = await axios.delete(`${API_BASE}/asterisk/ivr/${menuId}/options/${optId}`);
    return r.data;
  }

  // Time Conditions
  async getTimeConditions() {
    const r = await axios.get(`${API_BASE}/asterisk/time-conditions`);
    return r.data;
  }
  async createTimeCondition(data) {
    const r = await axios.post(`${API_BASE}/asterisk/time-conditions`, data);
    return r.data;
  }
  async updateTimeCondition(id, data) {
    const r = await axios.put(`${API_BASE}/asterisk/time-conditions/${id}`, data);
    return r.data;
  }
  async deleteTimeCondition(id) {
    const r = await axios.delete(`${API_BASE}/asterisk/time-conditions/${id}`);
    return r.data;
  }

  // Music on Hold
  async getMohClasses() {
    const r = await axios.get(`${API_BASE}/asterisk/moh`);
    return r.data;
  }
  async createMohClass(data) {
    const r = await axios.post(`${API_BASE}/asterisk/moh`, data);
    return r.data;
  }
  async updateMohClass(id, data) {
    const r = await axios.put(`${API_BASE}/asterisk/moh/${id}`, data);
    return r.data;
  }
  async deleteMohClass(id) {
    const r = await axios.delete(`${API_BASE}/asterisk/moh/${id}`);
    return r.data;
  }

  // Blacklist
  async getBlacklist() {
    const r = await axios.get(`${API_BASE}/asterisk/blacklist`);
    return r.data;
  }
  async createBlacklist(data) {
    const r = await axios.post(`${API_BASE}/asterisk/blacklist`, data);
    return r.data;
  }
  async updateBlacklist(id, data) {
    const r = await axios.put(`${API_BASE}/asterisk/blacklist/${id}`, data);
    return r.data;
  }
  async deleteBlacklist(id) {
    const r = await axios.delete(`${API_BASE}/asterisk/blacklist/${id}`);
    return r.data;
  }

  // Ring Groups (Asterisk admin view with members)
  async getAsteriskRingGroups() {
    const r = await axios.get(`${API_BASE}/asterisk/ring-groups`);
    return r.data;
  }
  async createRingGroup(data) {
    const r = await axios.post(`${API_BASE}/ring-groups`, data);
    return r.data;
  }
  async updateRingGroup(id, data) {
    const r = await axios.put(`${API_BASE}/ring-groups/${id}`, data);
    return r.data;
  }
  async deleteRingGroup(id) {
    const r = await axios.delete(`${API_BASE}/ring-groups/${id}`);
    return r.data;
  }
  async addRingGroupMember(groupId, data) {
    const r = await axios.post(`${API_BASE}/ring-groups/${groupId}/members`, data);
    return r.data;
  }
  async removeRingGroupMember(groupId, extId) {
    const r = await axios.delete(`${API_BASE}/ring-groups/${groupId}/members/${extId}`);
    return r.data;
  }

  // Sync queue
  async getSyncQueue(status, limit = 100) {
    const params = new URLSearchParams({ limit });
    if (status) params.set('status', status);
    const r = await axios.get(`${API_BASE}/asterisk/sync-queue?${params}`);
    return r.data;
  }
  async getSyncQueueStats() {
    const r = await axios.get(`${API_BASE}/asterisk/sync-queue/stats`);
    return r.data;
  }
  async triggerSyncNow() {
    const r = await axios.post(`${API_BASE}/asterisk/sync-now`);
    return r.data;
  }
  async deleteSyncItem(id) {
    const r = await axios.delete(`${API_BASE}/asterisk/sync-queue/${id}`);
    return r.data;
  }
  async retrySyncItem(id) {
    const r = await axios.post(`${API_BASE}/asterisk/sync-queue/${id}/retry`);
    return r.data;
  }

  // Asterisk-scoped tenant list (for ast_admin selectors)
  async getAsteriskTenants() {
    const r = await axios.get(`${API_BASE}/asterisk/tenants`);
    return r.data;
  }

  // Diagnostics
  async getRegisteredDevices() {
    const r = await axios.get(`${API_BASE}/asterisk/registered-devices`);
    return r.data;
  }
  async getTrunkStatus() {
    const r = await axios.get(`${API_BASE}/asterisk/trunk-status`);
    return r.data;
  }
  async qualifyAor(aorName) {
    const r = await axios.post(`${API_BASE}/asterisk/qualify/${encodeURIComponent(aorName)}`);
    return r.data;
  }
  async icmpPing(host, count = 4) {
    const r = await axios.post(`${API_BASE}/asterisk/tools/ping`, { host, count });
    return r.data;
  }
  async sipOptionsPing(aorName) {
    const r = await axios.post(`${API_BASE}/asterisk/tools/sip-options`, { aor_name: aorName });
    return r.data;
  }
  async getRecentFailures(limit = 50) {
    const r = await axios.get(`${API_BASE}/asterisk/recent-failures?limit=${limit}`);
    return r.data;
  }

  async chanspy(targetExtension, mode = 'listen') {
    const response = await axios.post(`${API_BASE}/supervisor/chanspy`, {
      target_extension: targetExtension,
      mode
    });
    return response.data;
  }

  async originateCall(destination, callerId) {
    const response = await axios.post(
      `${API_BASE}/calls/originate`,
      {
        destination,
        callerId
      }
    );

    return response.data;
  }

  /*
    CRM
  */

  async crmScreenpop(phone) {
    const response = await axios.get(`${API_BASE}/crm/screenpop?phone=${encodeURIComponent(phone)}`);
    return response.data;
  }

  async getCrmCustomers(params = {}) {
    const q = new URLSearchParams(params).toString();
    const response = await axios.get(`${API_BASE}/crm/customers${q ? '?' + q : ''}`);
    return response.data;
  }

  async getCrmCustomer(id) {
    const response = await axios.get(`${API_BASE}/crm/customers/${id}`);
    return response.data;
  }

  async createCrmCustomer(data) {
    const response = await axios.post(`${API_BASE}/crm/customers`, data);
    return response.data;
  }

  async updateCrmCustomer(id, data) {
    const response = await axios.put(`${API_BASE}/crm/customers/${id}`, data);
    return response.data;
  }

  async deleteCrmCustomer(id) {
    const response = await axios.delete(`${API_BASE}/crm/customers/${id}`);
    return response.data;
  }

  async autoCreateCrmCustomer(data) {
    const response = await axios.post(`${API_BASE}/crm/customers/auto-create`, data);
    return response.data;
  }

  async crmSearch(q) {
    const response = await axios.get(`${API_BASE}/crm/search?q=${encodeURIComponent(q)}`);
    return response.data;
  }

  async getCustomerTimeline(id) {
    const response = await axios.get(`${API_BASE}/crm/customers/${id}/timeline`);
    return response.data;
  }

  async getCrmNotes(customerId) {
    const response = await axios.get(`${API_BASE}/crm/notes?customer_id=${customerId}`);
    return response.data;
  }

  async saveCrmNote(data) {
    const response = await axios.post(`${API_BASE}/crm/notes`, data);
    return response.data;
  }

  async getCrmCallNotes(params = {}) {
    const q = new URLSearchParams(params).toString();
    const response = await axios.get(`${API_BASE}/crm/call-notes${q ? '?' + q : ''}`);
    return response.data;
  }

  async saveCrmCallNote(data) {
    const response = await axios.post(`${API_BASE}/crm/call-notes`, data);
    return response.data;
  }

  async getCrmTasks(customerId) {
    const response = await axios.get(`${API_BASE}/crm/tasks?customer_id=${customerId}`);
    return response.data;
  }

  async createCrmTask(data) {
    const response = await axios.post(`${API_BASE}/crm/tasks`, data);
    return response.data;
  }

  async updateCrmTask(id, data) {
    const response = await axios.put(`${API_BASE}/crm/tasks/${id}`, data);
    return response.data;
  }

  async getCompanies() {
    const response = await axios.get(`${API_BASE}/crm/companies`);
    return response.data;
  }

  async getCrmTags() {
    const response = await axios.get(`${API_BASE}/crm/tags`);
    return response.data;
  }

  /*
    REPORTS & ANALYTICS
  */

  async getReportsMeta(viewTenantId = null) {
    const q = viewTenantId ? `?view_tenant_id=${viewTenantId}` : '';
    const response = await axios.get(`${API_BASE}/reports/meta${q}`);
    return response.data;
  }

  async getReportCallVolume(params = {}) {
    const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))).toString();
    const response = await axios.get(`${API_BASE}/reports/call-volume${q ? '?' + q : ''}`);
    return response.data;
  }

  async getReportQueuePerformance(params = {}) {
    const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))).toString();
    const response = await axios.get(`${API_BASE}/reports/queue-performance${q ? '?' + q : ''}`);
    return response.data;
  }

  async getReportSla(params = {}) {
    const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))).toString();
    const response = await axios.get(`${API_BASE}/reports/sla${q ? '?' + q : ''}`);
    return response.data;
  }

  async getReportAgentPerformance(params = {}) {
    const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))).toString();
    const response = await axios.get(`${API_BASE}/reports/agent-performance${q ? '?' + q : ''}`);
    return response.data;
  }

  async getReportAbandonedCalls(params = {}) {
    const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))).toString();
    const response = await axios.get(`${API_BASE}/reports/abandoned-calls${q ? '?' + q : ''}`);
    return response.data;
  }

  async getReportMissedCalls(params = {}) {
    const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))).toString();
    const response = await axios.get(`${API_BASE}/reports/missed-calls${q ? '?' + q : ''}`);
    return response.data;
  }

  async triggerAggregation(date = null, viewTenantId = null) {
    const body = {};
    if (date) body.date = date;
    if (viewTenantId) body.view_tenant_id = viewTenantId;
    const response = await axios.post(`${API_BASE}/reports/trigger-aggregation`, body);
    return response.data;
  }

  exportReport(reportName, params = {}) {
    const token = this.getToken();
    const p = { ...params, fmt: 'csv' };
    if (token) p.token = token;
    const q = new URLSearchParams(Object.fromEntries(Object.entries(p).filter(([, v]) => v != null))).toString();
    const url = `${API_BASE}/reports/${reportName}?${q}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportName}.csv`;
    a.click();
  }

  /* TENANT BRANDING */

  async getTenantBranding(tenantId) {
    const response = await axios.get(`${API_BASE}/admin/tenants/${tenantId}/branding`);
    return response.data;
  }

  async updateTenantBranding(tenantId, data) {
    const response = await axios.put(`${API_BASE}/admin/tenants/${tenantId}/branding`, data);
    return response.data;
  }

  /* PHONE PROVISIONING */

  async getPhonesDashboard() {
    const r = await axios.get(`${API_BASE}/phones/dashboard`);
    return r.data;
  }
  async getPhones() {
    const r = await axios.get(`${API_BASE}/phones`);
    return r.data;
  }
  async getPhone(id) {
    const r = await axios.get(`${API_BASE}/phones/${id}`);
    return r.data;
  }
  async createPhone(data) {
    const r = await axios.post(`${API_BASE}/phones`, data);
    return r.data;
  }
  async updatePhone(id, data) {
    const r = await axios.put(`${API_BASE}/phones/${id}`, data);
    return r.data;
  }
  async deletePhone(id) {
    const r = await axios.delete(`${API_BASE}/phones/${id}`);
    return r.data;
  }
  async getPhoneConfig(id) {
    const r = await axios.get(`${API_BASE}/phones/${id}/config`);
    return r.data;
  }
  async generatePhoneConfig(id) {
    const r = await axios.post(`${API_BASE}/phones/${id}/generate`);
    return r.data;
  }
  async rebootPhone(id) {
    const r = await axios.post(`${API_BASE}/phones/${id}/reboot`);
    return r.data;
  }
  async getPhoneStatus(id) {
    const r = await axios.get(`${API_BASE}/phones/${id}/status`);
    return r.data;
  }
  async getPhoneModels() {
    const r = await axios.get(`${API_BASE}/phones/models`);
    return r.data;
  }
  async getFirmwareList() {
    const r = await axios.get(`${API_BASE}/phones/firmware/list`);
    return r.data;
  }
  async uploadFirmware(formData) {
    const r = await axios.post(`${API_BASE}/phones/firmware/upload`, formData);
    return r.data;
  }
  async deleteFirmware(id) {
    const r = await axios.delete(`${API_BASE}/phones/firmware/${id}`);
    return r.data;
  }
  async getCertProfiles() {
    const r = await axios.get(`${API_BASE}/phones/certificates`);
    return r.data;
  }
  async createCertProfile(data) {
    const r = await axios.post(`${API_BASE}/phones/certificates`, data);
    return r.data;
  }
  async deleteCertProfile(id) {
    const r = await axios.delete(`${API_BASE}/phones/certificates/${id}`);
    return r.data;
  }
  async getPhoneAuditLogs(phoneId, limit = 100) {
    const q = new URLSearchParams({ limit });
    if (phoneId) q.set('phone_id', phoneId);
    const r = await axios.get(`${API_BASE}/phones/audit-logs?${q}`);
    return r.data;
  }

  async uploadTenantAsset(tenantId, asset, file) {
    const form = new FormData();
    form.append('file', file);
    // Do NOT set Content-Type manually — let the browser set it with the boundary
    const response = await axios.post(
      `${API_BASE}/admin/tenants/${tenantId}/branding/${asset}`,
      form
    );
    return response.data;
  }

  async resizeTenantAsset(tenantId, asset, dims) {
    const response = await axios.post(
      `${API_BASE}/admin/tenants/${tenantId}/branding/${asset}/resize`,
      dims
    );
    return response.data;
  }

  async setTeamsPresence(availability, activity) {
    const response = await axios.post(`${API_BASE}/teams/me/set-presence`, { availability, activity });
    return response.data;
  }
}

const apiService = new ApiService();

export default apiService;